import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Card, CardBody, Progress, Alert } from 'reactstrap';
import { config } from '../../config/environment';

const PaymentVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'timeout' | 'error'>('verifying');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');

  const flowId = searchParams.get('flow');
  const provider = searchParams.get('provider') || 'stripe';
  const maxWaitTime = provider === 'razorpay' ? 300 : 120; // 5min for Razorpay, 2min for Stripe

  const checkFlowStatus = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.onboardingFlows}/${flowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        console.log('🔄 PaymentVerification - Flow status check:', flow.status);
        return flow;
      } else {
        throw new Error(`Failed to check flow status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching flow status:', error);
      throw error;
    }
  }, [flowId]);

  useEffect(() => {
    // Update message based on elapsed time
    if (elapsedTime < 30) {
      setCurrentMessage('Processing your payment...');
    } else if (elapsedTime < 120) {
      setCurrentMessage('Verifying payment details...');
    } else {
      setCurrentMessage('Nearly there, finalizing your subscription...');
    }
  }, [elapsedTime]);

  const startVerificationProcess = useCallback(async () => {
    const startTime = Date.now();
    const pollInterval = setInterval(async () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);

      if (elapsed >= maxWaitTime) {
        clearInterval(pollInterval);
        setVerificationStatus('timeout');
        return;
      }

      try {
        const flowData = await checkFlowStatus();
        if (flowData && flowData.status === 'PAID') {
          console.log('✅ PaymentVerification - Payment verified, redirecting to organization setup');
          clearInterval(pollInterval);
          navigate(`/create-organization?flow=${flowId}`);
          return;
        }
      } catch (error) {
        console.error('Error checking flow status:', error);
        if (elapsed > 60) { // Only set error after 1 minute of trying
          clearInterval(pollInterval);
          setVerificationStatus('error');
        }
      }
    }, 3000); // Poll every 3 seconds

    // Cleanup on unmount
    return () => clearInterval(pollInterval);
  }, [navigate, flowId, maxWaitTime, checkFlowStatus]);

  useEffect(() => {
    // Start the verification process when component mounts
    if (!flowId) {
      console.error('No flow ID provided for payment verification');
      setVerificationStatus('error');
      return;
    }

    console.log('🔄 PaymentVerification - Starting verification process for flow:', flowId);

    // Call the async function and handle cleanup
    let cleanup: (() => void) | undefined;

    const initVerification = async () => {
      cleanup = await startVerificationProcess();
    };

    initVerification();

    // Return cleanup function
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [flowId, startVerificationProcess]);

  const handleRetry = () => {
    setVerificationStatus('verifying');
    setElapsedTime(0);
    startVerificationProcess();
  };

  const handleContactSupport = () => {
    // You can customize this based on your support system
    window.open('mailto:support@mentorgain.com?subject=Payment Verification Issue&body=Flow ID: ' + flowId, '_blank');
  };

  const getProgressPercentage = () => {
    return Math.min((elapsedTime / maxWaitTime) * 100, 95); // Never show 100% until complete
  };

  const getProviderName = () => {
    return provider === 'razorpay' ? 'Razorpay' : 'Stripe';
  };

  if (verificationStatus === 'verifying') {
    return (
      <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="col-md-6 col-lg-5">
            <Card className="shadow-sm border-0">
              <CardBody className="p-5 text-center">
                {/* Header */}
                <div className="mb-4">
                  <div className="mb-3">
                    <Spinner size="lg" color="primary" />
                  </div>
                  <h3 className="text-primary mb-2">Setting up your subscription</h3>
                  <p className="text-muted mb-0">{currentMessage}</p>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <Progress
                    value={getProgressPercentage()}
                    color="primary"
                    className="mb-2"
                    style={{ height: '8px' }}
                  />
                  <small className="text-muted">
                    Processing via {getProviderName()}... ({elapsedTime}s)
                  </small>
                </div>

                {/* Information */}
                <div className="text-start">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-success rounded-circle me-3" style={{ width: '8px', height: '8px' }}></div>
                    <small className="text-muted">Payment initiated successfully</small>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <Spinner size="sm" color="primary" className="me-3" />
                    <small className="text-muted">Verifying payment with {getProviderName()}</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="bg-light rounded-circle me-3" style={{ width: '8px', height: '8px' }}></div>
                    <small className="text-muted">Preparing your workspace</small>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-4 pt-3 border-top">
                  <small className="text-muted">
                    This usually takes less than a minute. Please don't refresh the page.
                  </small>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'timeout') {
    return (
      <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="col-md-6 col-lg-5">
            <Card className="shadow-sm border-0">
              <CardBody className="p-5 text-center">
                <Alert color="warning" className="mb-4">
                  <h5 className="alert-heading">Payment verification taking longer than usual</h5>
                  <p className="mb-3">
                    Your payment is being processed, but it's taking longer than expected.
                    This sometimes happens with {getProviderName()} transactions.
                  </p>
                  <hr />
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={handleRetry}
                    >
                      Check Again
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleContactSupport}
                    >
                      Contact Support
                    </button>
                  </div>
                </Alert>

                <div className="text-muted">
                  <small>
                    <strong>What's happening:</strong><br />
                    Your payment was submitted successfully, but we're still waiting for confirmation
                    from {getProviderName()}. This can take a few minutes during high traffic periods.
                  </small>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="col-md-6 col-lg-5">
            <Card className="shadow-sm border-0">
              <CardBody className="p-5 text-center">
                <Alert color="danger" className="mb-4">
                  <h5 className="alert-heading">Unable to verify payment</h5>
                  <p className="mb-3">
                    We're having trouble connecting to our payment verification system.
                  </p>
                  <hr />
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleRetry}
                    >
                      Try Again
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => navigate(`/getting-started?flow=${flowId}`)}
                    >
                      Back to Plans
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleContactSupport}
                    >
                      Get Help
                    </button>
                  </div>
                </Alert>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PaymentVerification;